import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { spacing, fontStyles, colors } from '../../styles';
import Page from '../Page';
import Header from '../Header';
import * as storage from '../../storage';

interface RouteParams {
  id: string;
}

const UpdatePortfolioPage: React.FC = () => {
  const { id } = useParams<RouteParams>();
  const history = useHistory();
  const [portfolio, setPortfolio] = useState<storage.Portfolio | null>(null);
  const [strategy, setStrategy] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Load the portfolio data
  useEffect(() => {
    const loadPortfolio = async () => {
      try {
        const portfolios = await storage.getPortfolios();
        const found = portfolios.find((p) => p.id === id);

        if (found) {
          setPortfolio(found);
          setStrategy('');
        } else {
          console.error('Portfolio not found:', id);
        }
      } catch (error) {
        console.error('Error loading portfolio:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPortfolio();
  }, [id]);

  const handleSubmit = () => {
    if (!portfolio) return;

    // Navigate directly to edit page for manual portfolio management
    history.push(`/portfolios/edit/${id}`);
  };

  const handleEditHoldingsClick = () => {
    history.push(`/portfolios/edit/${id}`);
  };

  // Styles matching AddPortfolio
  const pageStyles = css`
    padding: ${spacing.milli};
    padding-bottom: calc(${spacing.regular} + ${spacing.giga});

    @media (max-width: 359px) {
      padding: ${spacing.micro} ${spacing.micro};
    }
  `;

  const headingStyles = css`
    ${fontStyles.shark};
    margin: 0;
    margin-top: ${spacing.centi};
    margin-bottom: ${spacing.deci};
  `;

  const instructionStyles = css`
    ${fontStyles.dog};
    color: ${colors.silver};
  `;

  const textareaStyles = css`
    width: 100%;
    border: 1px solid ${colors.cloud};
    border-radius: 8px;
    padding: ${spacing.deci};
    ${fontStyles.cat};
    margin-top: ${spacing.deka};
    background-color: ${colors.sugar};
    color: ${colors.liquorice};
    min-height: 160px;
    resize: vertical;

    html[data-theme='dark'] & {
      background-color: #242424;
      color: ${colors.sugar};
      border-color: ${colors.tungsten};
    }

    &:focus {
      outline: none;
      border-color: ${colors.petrolBlue};
    }

    &::placeholder {
      ${fontStyles.cat};
      color: ${colors.silver};
    }
  `;

  const buttonStyles = css`
    display: block;
    width: 100%;
    background-color: ${colors.petrolBlue};
    color: ${colors.snow};
    border: none;
    border-radius: 8px;
    padding: ${spacing.deci};
    ${fontStyles.horse};
    margin-top: ${spacing.kilo};
    margin-bottom: ${spacing.deka};
    cursor: pointer;
    opacity: 1;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
      opacity: 0.9;
    }

    @media (max-width: 359px) {
      padding: ${spacing.centi} ${spacing.deci};
      font-size: 14px;
    }
  `;

  const editButtonStyles = css`
    display: block;
    width: 100%;
    background: none;
    border: none;
    color: ${colors.petrolBlue};
    ${fontStyles.dog};
    text-align: center;
    margin-top: ${spacing.deci};
    cursor: pointer;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }

    html[data-theme='dark'] & {
      color: ${colors.skyBlue}; // Lighter blue for dark mode
    }
  `;

  if (isLoading) {
    return (
      <Page>
        <Header headingText="" onBack={() => history.push(`/portfolios/view/${id}`)} />
        <div css={pageStyles}>
          <p css={instructionStyles}>Loading portfolio data...</p>
        </div>
      </Page>
    );
  }

  if (!portfolio) {
    return (
      <Page>
        <Header headingText="" onBack={() => history.push('/portfolios')} />
        <div css={pageStyles}>
          <p css={instructionStyles}>Portfolio not found. Please try again.</p>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <Header headingText="" onBack={() => history.push(`/portfolios/view/${id}`)} />
      <div css={pageStyles}>
        <h1 css={headingStyles}>Update {portfolio.title}</h1>

        <p css={instructionStyles}>
          Enter additional instructions to update your portfolio based on current market conditions:
        </p>

        <textarea
          css={textareaStyles}
          value={strategy}
          onChange={(e) => setStrategy(e.target.value)}
          placeholder="For example: Reduce risk, focus more on growth, add exposure to AI sector, etc. Or leave empty to simply refresh with current market data."
        />

        <button css={buttonStyles} onClick={handleSubmit}>
          Update Portfolio
        </button>
        <button css={editButtonStyles} onClick={handleEditHoldingsClick}>
          Edit Manually
        </button>
      </div>
    </Page>
  );
};

export default UpdatePortfolioPage;
